import type {
  IntakeFormField,
  IntakeFormSection,
  ShowIfCondition,
  SignatureField,
} from '../../api/bookingApi';
import {
  MAX_UPLOADS_PER_SUBMISSION,
  isFileUploadFieldKey,
  type V2FormItem,
  type V2Item,
  type V2Package,
} from './types';

// Fail-closed validation of a resolved `form_package_v2` payload (backend#2049).
//
// Anything this SPA does not understand — a new form kind, a new schema
// version, a field type it cannot render, a file field it cannot stage — makes
// the whole package unrenderable and the page shows the call-the-office
// screen. It never falls through to the v1 renderer. Reasons are closed
// strings for the console; `detail` carries ids/keys only, never content.

export type V2InvalidReason =
  | 'missing_ids'
  | 'schema_version'
  | 'empty_items'
  | 'ordinals'
  | 'duplicate_item_id'
  | 'bad_item'
  | 'unsupported_form_kind'
  | 'malformed_agreement'
  | 'malformed_form_template'
  | 'unsupported_field_type'
  | 'duplicate_field_key'
  | 'unsupported_file_upload_key'
  | 'missing_options'
  | 'malformed_show_if'
  | 'upload_budget';

export type V2ValidationResult =
  | { ok: true; pkg: V2Package }
  | { ok: false; reason: V2InvalidReason; detail: string };

type KnownFieldType = IntakeFormField['field_type'];

const KNOWN_FIELD_TYPES: readonly KnownFieldType[] = [
  'text', 'email', 'phone', 'number', 'date', 'select', 'multiselect', 'checkbox',
  'textarea', 'address', 'signature', 'initials', 'file_upload', 'static_text',
];

// Legacy builder types that exist in real templates and that CustomFormField's
// default branch renders as plain text inputs.
const TEXT_ALIAS_FIELD_TYPES: Record<string, KnownFieldType> = {
  first_name: 'text',
  last_name: 'text',
};

const SIGNATURE_FIELD_TYPES: readonly SignatureField['type'][] = ['signature', 'date', 'text', 'initials'];
// Keyed by the item types' own kinds: a kind added to V2Item but not here, or here
// but not to V2Item, fails to compile.
const SUPPORTED_FORM_KINDS: Record<V2Item['kind'], true> = {
  intake: true,
  agreement: true,
  custom: true,
  insurance_update: true,
};

function isSupportedFormKind(kind: unknown): kind is V2Item['kind'] {
  return typeof kind === 'string' && Object.prototype.hasOwnProperty.call(SUPPORTED_FORM_KINDS, kind);
}

type Failure = { ok: false; reason: V2InvalidReason; detail: string };

function fail(reason: V2InvalidReason, detail = ''): Failure {
  return { ok: false, reason, detail };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function adaptSignatureFields(raw: unknown, itemId: string): SignatureField[] | Failure {
  if (raw === null || raw === undefined) return [];
  if (!Array.isArray(raw)) return fail('malformed_agreement', `${itemId}:signature_fields`);
  const out: SignatureField[] = [];
  for (let i = 0; i < raw.length; i += 1) {
    const sf = raw[i];
    if (!isRecord(sf) || typeof sf.label !== 'string') {
      return fail('malformed_agreement', `${itemId}:signature_fields[${i}]`);
    }
    const type = sf.type;
    if (typeof type !== 'string' || SIGNATURE_FIELD_TYPES.indexOf(type as SignatureField['type']) === -1) {
      return fail('malformed_agreement', `${itemId}:signature_fields[${i}].type`);
    }
    out.push({ label: sf.label, type: type as SignatureField['type'], required: sf.required === true });
  }
  return out;
}

function adaptField(raw: unknown, itemId: string, index: string): IntakeFormField | Failure {
  if (!isRecord(raw)) return fail('malformed_form_template', `${itemId}:${index}`);
  if (!nonEmptyString(raw.key)) return fail('malformed_form_template', `${itemId}:${index}.key`);
  const key = raw.key;
  const rawType = typeof raw.field_type === 'string' ? raw.field_type : '';
  const fieldType = TEXT_ALIAS_FIELD_TYPES[rawType] ?? (rawType as KnownFieldType);
  if (KNOWN_FIELD_TYPES.indexOf(fieldType) === -1) {
    return fail('unsupported_field_type', `${itemId}:${key}:${rawType}`);
  }
  let options: string[] | null = null;
  if (Array.isArray(raw.options)) {
    options = raw.options.filter((o): o is string => typeof o === 'string' && o.trim().length > 0);
  } else if (raw.options !== null && raw.options !== undefined) {
    return fail('malformed_form_template', `${itemId}:${key}.options`);
  }
  if ((fieldType === 'select' || fieldType === 'multiselect') && (!options || options.length === 0)) {
    return fail('missing_options', `${itemId}:${key}`);
  }
  if (fieldType === 'file_upload' && !isFileUploadFieldKey(key)) {
    return fail('unsupported_file_upload_key', `${itemId}:${key}`);
  }
  const acceptedTypes = Array.isArray(raw.accepted_file_types)
    ? raw.accepted_file_types.filter((t): t is string => typeof t === 'string')
    : null;
  // Optional conditional visibility. A malformed rule fails the whole package
  // closed rather than silently always-showing (dozens of stray fields is the
  // very problem show_if solves).
  let showIf: ShowIfCondition | null = null;
  if (raw.show_if !== null && raw.show_if !== undefined) {
    const s = raw.show_if;
    if (!isRecord(s) || Object.keys(s).length !== 2
        || !Object.prototype.hasOwnProperty.call(s, 'field') || !Object.prototype.hasOwnProperty.call(s, 'equals')
        || !nonEmptyString(s.field) || !(typeof s.equals === 'string' || typeof s.equals === 'boolean')) {
      return fail('malformed_show_if', `${itemId}:${key}.show_if`);
    }
    showIf = { field: s.field, equals: s.equals };
  }
  return {
    key,
    label: typeof raw.label === 'string' ? raw.label : key,
    field_type: fieldType,
    required: raw.required === true,
    placeholder: optionalString(raw.placeholder),
    options,
    ehr_mapping: optionalString(raw.ehr_mapping),
    accepted_file_types: acceptedTypes,
    max_file_size_mb: typeof raw.max_file_size_mb === 'number' ? raw.max_file_size_mb : null,
    content: fieldType === 'static_text' ? optionalString(raw.content) : null,
    must_be_true: raw.must_be_true === true,
    show_if: showIf,
    // Only a text field can be the insurance-company typeahead; anywhere else the
    // declaration is ignored and the field renders as it always has.
    payer_id_key: fieldType === 'text' && nonEmptyString(raw.payer_id_key) ? raw.payer_id_key.trim() : null,
  };
}

function adaptSections(raw: unknown, itemId: string): IntakeFormSection[] | Failure {
  if (!isRecord(raw) || !Array.isArray(raw.sections)) {
    return fail('malformed_form_template', `${itemId}:form_template`);
  }
  // Conditional controllers must use the backend's stable integer-or-zero walk.
  // Preserve legacy adaptation for templates without conditions.
  const conditional = raw.sections.some((section) => isRecord(section) && Array.isArray(section.fields)
    && section.fields.some((field) => isRecord(field) && field.show_if != null));
  const sections: IntakeFormSection[] = [];
  const seenKeys: Record<string, true> = {};
  for (let s = 0; s < raw.sections.length; s += 1) {
    const section = raw.sections[s];
    if (!isRecord(section) || !Array.isArray(section.fields)) {
      return fail('malformed_form_template', `${itemId}:sections[${s}]`);
    }
    const fields: IntakeFormField[] = [];
    for (let f = 0; f < section.fields.length; f += 1) {
      const adapted = adaptField(section.fields[f], itemId, `sections[${s}].fields[${f}]`);
      if ('ok' in adapted) return adapted;
      if (seenKeys[adapted.key]) return fail('duplicate_field_key', `${itemId}:${adapted.key}`);
      seenKeys[adapted.key] = true;
      fields.push(adapted);
    }
    sections.push({
      key: typeof section.key === 'string' ? section.key : `section-${s}`,
      label: typeof section.label === 'string' ? section.label : '',
      sort_order: conditional ? (Number.isInteger(section.sort_order) ? section.sort_order as number : 0)
        : typeof section.sort_order === 'number' ? section.sort_order : s,
      fields,
    });
  }
  // A payer companion is an extra answer key in the item, so it must not shadow a
  // field or another companion; an ambiguous declaration is dropped (plain text).
  const companionCounts: Record<string, number> = {};
  sections.forEach((section) => section.fields.forEach((field) => {
    if (field.payer_id_key) companionCounts[field.payer_id_key] = (companionCounts[field.payer_id_key] ?? 0) + 1;
  }));
  sections.forEach((section) => section.fields.forEach((field) => {
    if (field.payer_id_key && (seenKeys[field.payer_id_key] || companionCounts[field.payer_id_key] > 1)) {
      field.payer_id_key = null;
    }
  }));
  // Stable sort by sort_order (Array.prototype.sort is stable in every supported browser).
  sections.sort((a, b) => a.sort_order - b.sort_order);
  const earlier = new Map<string, IntakeFormField>();
  for (const section of sections) {
    for (const field of section.fields) {
      if (field.show_if) {
        const controller = earlier.get(field.show_if.field);
        const valid = controller?.field_type === 'select'
          ? typeof field.show_if.equals === 'string' && controller.options?.includes(field.show_if.equals)
          : controller?.field_type === 'checkbox' && typeof field.show_if.equals === 'boolean';
        if (!valid) return fail('malformed_show_if', `${itemId}:${field.key}.show_if`);
      }
      earlier.set(field.key, field);
    }
  }
  return sections;
}

function countUploadFields(item: V2Item): number {
  let count = 0;
  if (item.kind === 'agreement') {
    count += item.signatureFields.filter((sf) => sf.type === 'signature').length;
  }
  item.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.field_type === 'signature' || field.field_type === 'initials' || field.field_type === 'file_upload') {
        count += 1;
      }
    });
  });
  return count;
}

/**
 * Turn the raw resolver response into a `V2Package`, or say exactly why it cannot
 * be rendered. Pure: no I/O, no logging.
 */
export function adaptAndValidateV2Package(raw: unknown): V2ValidationResult {
  if (!isRecord(raw)) return fail('missing_ids', 'not an object');
  if (!nonEmptyString(raw.token) || !nonEmptyString(raw.client_id) || !nonEmptyString(raw.package_instance_id)) {
    return fail('missing_ids');
  }
  if (raw.schema_version !== 1) return fail('schema_version', String(raw.schema_version));
  if (!Array.isArray(raw.items)) return fail('missing_ids', 'items');
  if (raw.items.length === 0) return fail('empty_items');

  const ordinals: number[] = [];
  for (let i = 0; i < raw.items.length; i += 1) {
    const item = raw.items[i];
    if (!isRecord(item) || !Number.isInteger(item.ordinal)) return fail('ordinals', `items[${i}]`);
    ordinals.push(item.ordinal as number);
  }
  const sortedOrdinals = ordinals.slice().sort((a, b) => a - b);
  for (let i = 0; i < sortedOrdinals.length; i += 1) {
    if (sortedOrdinals[i] !== i) return fail('ordinals', sortedOrdinals.join(','));
  }

  const rawItems = (raw.items as Record<string, unknown>[]).slice().sort(
    (a, b) => (a.ordinal as number) - (b.ordinal as number),
  );
  const items: V2Item[] = [];
  const seenIds: Record<string, true> = {};
  let hasAgreements = false;

  for (let i = 0; i < rawItems.length; i += 1) {
    const item = rawItems[i];
    if (!nonEmptyString(item.instance_item_id)) return fail('bad_item', `items[${i}].instance_item_id`);
    const id = item.instance_item_id;
    if (seenIds[id]) return fail('duplicate_item_id', id);
    seenIds[id] = true;
    if (typeof item.name !== 'string') return fail('bad_item', `${id}:name`);
    if (typeof item.required !== 'boolean') return fail('bad_item', `${id}:required`);
    const kind = item.form_kind;
    if (!isSupportedFormKind(kind)) return fail('unsupported_form_kind', `${id}:${String(kind)}`);
    if (!isRecord(item.content)) return fail('bad_item', `${id}:content`);
    const content = item.content;
    const base = { instanceItemId: id, ordinal: item.ordinal as number, name: item.name, required: item.required };
    const template = content.form_template;
    const insuranceStatusNoRelaxesRequired = isRecord(template) && template.insurance_status_no_relaxes_required === true;
    if (insuranceStatusNoRelaxesRequired) {
      const status = Array.isArray(template.sections)
        ? template.sections.flatMap((section) => isRecord(section) && Array.isArray(section.fields) ? section.fields : [])
          .find((field) => isRecord(field) && field.key === 'insurance_status')
        : undefined;
      if (kind !== 'intake' || !isRecord(status) || status.field_type !== 'select'
        || !Array.isArray(status.options) || status.options.length !== 2
        || !status.options.includes('Yes') || !status.options.includes('No')) {
        return fail('malformed_form_template', `${id}:insurance_status`);
      }
    }

    if (kind === 'agreement') {
      // Two authoring shapes exist in production: legacy text + signature_fields, and
      // form-builder templates (static_text paragraphs + signature/date/… fields).
      if (nonEmptyString(content.content)) {
        const signatureFields = adaptSignatureFields(content.signature_fields, id);
        if (!Array.isArray(signatureFields)) return signatureFields;
        items.push({ kind: 'agreement', mode: 'text', ...base, content: content.content, signatureFields, sections: [] });
      } else {
        if (!isRecord(content.form_template)) return fail('malformed_agreement', `${id}:content`);
        const sections = adaptSections(content.form_template, id);
        if (!Array.isArray(sections)) return sections;
        for (const section of sections) {
          for (const field of section.fields) {
            if (field.field_type === 'static_text' && field.show_if) return fail('malformed_show_if', `${id}:${field.key}.show_if`);
          }
        }
        if (!sections.some((s) => s.fields.length > 0)) return fail('malformed_agreement', `${id}:empty_template`);
        items.push({ kind: 'agreement', mode: 'template', ...base, content: '', signatureFields: [], sections });
      }
      hasAgreements = true;
    } else {
      const sections = adaptSections(content.form_template, id);
      if (!Array.isArray(sections)) return sections;
      const form: V2FormItem = { kind, ...base, sections, insuranceStatusNoRelaxesRequired };
      items.push(form);
    }
  }

  let uploadBudget = items.reduce((sum, item) => sum + countUploadFields(item), 0);
  if (hasAgreements) uploadBudget += 1; // the final consent signature
  if (uploadBudget > MAX_UPLOADS_PER_SUBMISSION) return fail('upload_budget', String(uploadBudget));

  return {
    ok: true,
    pkg: { packageInstanceId: raw.package_instance_id, items, hasAgreements, uploadBudget },
  };
}
